import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

export interface WBSDeviationReportItem {
  id: number;
  activity_name: string;
  activity_code?: string;
  description?: string;
  project_id: number;
  project_name: string;
  project_identifier?: string;
  assigned_to?: string;
  activity_order?: number;
  plan_start_date: string;
  plan_end_date: string;
  actual_start_date?: string;
  actual_end_date?: string;
  percent_complete: number;
  planned_duration?: number;
  actual_duration?: number;
  start_date_deviation?: number;
  end_date_deviation?: number;
  duration_deviation?: number;
  expected_percent_complete?: number;
  progress_deviation?: number;
  start_date_status: 'not_started' | 'delayed' | 'early' | 'on_time';
  end_date_status: 'not_completed' | 'delayed' | 'early' | 'on_time';
  duration_status: 'unknown' | 'over' | 'under' | 'on_time';
  progress_status: 'unknown' | 'ahead' | 'behind' | 'on_track';
}

export interface WBSDeviationReportData {
  group_by: 'activity' | 'project';
  reports: WBSDeviationReportItem[] | any[];
  summary: {
    total_activities: number;
    total_projects: number;
    activities_delayed_start: number;
    activities_early_start: number;
    activities_on_time_start: number;
    activities_delayed_end: number;
    activities_early_end: number;
    activities_on_time_end: number;
    activities_behind_schedule: number;
    activities_ahead_schedule: number;
    activities_on_track: number;
    avg_start_date_deviation: number;
    avg_end_date_deviation: number;
    avg_duration_deviation: number;
    avg_progress_deviation: number;
  };
}

@Component({
  selector: 'app-wbs-deviation-report',
  templateUrl: './wbs-deviation-report.component.html',
  styleUrls: ['./wbs-deviation-report.component.css']
})
export class WBSDeviationReportComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  reportData: WBSDeviationReportData | null = null;
  reports: WBSDeviationReportItem[] = [];
  loading = false;
  groupBy: 'activity' | 'project' = 'activity';
  
  selectedProject: string = '';
  startDate: string = '';
  endDate: string = '';
  projects: any[] = [];

  constructor(
    private apiService: ApiService,
    private toastr: ToastrMsgService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.loadProjects();
    this.route.queryParams.subscribe(params => {
      if (params['project_id']) {
        this.selectedProject = params['project_id'];
      }
      this.generateReport();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProjects(): void {
    this.apiService.getProjectCommonAll({ limit: 1000 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.projects = res.data || res || [];
        },
        error: (err) => {
          console.error('Error loading projects:', err);
        }
      });
  }

  generateReport(): void {
    this.loading = true;
    const params: any = {
      group_by: this.groupBy
    };
    
    if (this.selectedProject) params.project_id = this.selectedProject;
    if (this.startDate) params.start_date = this.startDate;
    if (this.endDate) params.end_date = this.endDate;

    this.apiService.getWBSDeviationReport(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.reportData = response.data;
            this.reports = this.groupBy === 'activity' 
              ? (response.data.reports as WBSDeviationReportItem[])
              : [];
            this.loading = false;
          } else {
            this.toastr.error(response.message || 'Failed to generate deviation report');
            this.loading = false;
          }
        },
        error: (err) => {
          console.error('Error generating deviation report:', err);
          this.toastr.error(err.error?.message || 'Failed to generate deviation report');
          this.loading = false;
        }
      });
  }

  onGroupByChange(): void {
    this.generateReport();
  }

  onFilterChange(): void {
    this.generateReport();
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  /**
   * CSS class for numeric deviation display (spans only).
   * Date/duration: positive = late/over (bad), negative = early/under (good).
   * Progress %: positive = ahead of expected curve (good), negative = behind (bad) — use treatAsProgress.
   */
  getDeviationClass(
    deviation: number | null | undefined,
    status?: string,
    opts?: { treatAsProgress?: boolean }
  ): string {
    if (deviation === null || deviation === undefined) return 'deviation-unknown';
    if (status === 'delayed' || status === 'behind' || status === 'over') return 'deviation-negative';
    if (status === 'early' || status === 'ahead' || status === 'under') return 'deviation-positive';
    if (status === 'on_time' || status === 'on_track') return 'deviation-match';

    if (opts?.treatAsProgress) {
      if (deviation > 0) return 'deviation-positive';
      if (deviation < 0) return 'deviation-negative';
      return 'deviation-match';
    }
    if (deviation > 0) return 'deviation-negative';
    if (deviation < 0) return 'deviation-positive';
    return 'deviation-match';
  }

  /** Progress % deviation coloring for summary / cells (sign semantics differ from date metrics). */
  getProgressDeviationClass(deviation: number | null | undefined, status?: string): string {
    return this.getDeviationClass(deviation, status, { treatAsProgress: true });
  }

  /** Subtle row tint only — do not use deviation-negative/positive on <tr> (those set text color and inherit). */
  getRowHighlightClass(item: WBSDeviationReportItem): string {
    switch (item.progress_status) {
      case 'behind':
        return 'row-highlight-behind';
      case 'ahead':
        return 'row-highlight-ahead';
      case 'on_track':
        return 'row-highlight-on-track';
      default:
        return 'row-highlight-neutral';
    }
  }

  getDeviationIcon(status: string): string {
    if (status === 'delayed' || status === 'behind' || status === 'over') return 'arrow_downward';
    if (status === 'early' || status === 'ahead' || status === 'under') return 'arrow_upward';
    if (status === 'on_time' || status === 'on_track') return 'check_circle';
    return 'help_outline';
  }

  getStatusBadgeClass(status: string): string {
    if (status === 'delayed' || status === 'behind' || status === 'over') return 'badge-danger';
    if (status === 'early' || status === 'ahead' || status === 'under') return 'badge-success';
    if (status === 'on_time' || status === 'on_track') return 'badge-info';
    return 'badge-secondary';
  }

  getStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      'delayed': 'Delayed',
      'early': 'Early',
      'on_time': 'On Time',
      'not_started': 'Not Started',
      'not_completed': 'Not Completed',
      'behind': 'Behind',
      'ahead': 'Ahead',
      'on_track': 'On Track',
      'over': 'Over',
      'under': 'Under',
      'unknown': 'Unknown'
    };
    return statusMap[status] || status;
  }

  /**
   * Destination for the page header back button. Returns the
   * project-specific WBS view when a project is selected, otherwise
   * falls back to the projects list.
   */
  get backLink(): any[] {
    return this.selectedProject
      ? ['/home/work-breakdown-structure', this.selectedProject]
      : ['/home/projects'];
  }
}



